import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    persisted: { type: Boolean, default: false },
    statesUrl: { type: String, default: "/api/states" },
    citiesUrl: { type: String, default: "/api/states/cities" },
    noStatesMessage: { type: String, default: "No states available" },
    noCitiesMessage: { type: String, default: "No cities available" }
  }

  static classes = ["hidden", "loading"]

  static targets = [
    "locationSelector",
    "displayLocation",
    "countrySelect",
    "stateSelect",
    "stateContainer",
    "locationText",
    "selectorButton"
  ]

  // Constants for timing and retries
  DELAYS = {
    TOMSELECT_SYNC: 150,
    TOMSELECT_INIT: 100,
    STATE_LOAD: 50
  }

  RETRIES = {
    TOMSELECT_READY: 10
  }

  async connect() {
    this.validateRequiredTargets()
    this.countriesBeforeAdd = this.getSelectedCountries()
    this.setupCountryChangeListener()
    this.setupStateChangeListener()

    const selectedCountries = this.getSelectedCountries()
    
    if (selectedCountries.length === 0) {
      this.hideStateSelection()
      return
    }
    
    if (selectedCountries.length === 1) {
      await this.loadStates(selectedCountries[0])
      this.showStateSelection()
      await this.restoreSavedStates()
    } else {
      this.hideStateSelection()
    }
  }

  // ==================== Helper Methods ====================

  getSelectedCountries() {
    if (!this.hasCountrySelectTarget) return []
    
    if (this.countrySelectTarget.tomselect) {
      const values = this.countrySelectTarget.tomselect.getValue()
      return Array.isArray(values) ? values : (values ? [values] : [])
    }
    
    if (this.countrySelectTarget.multiple) {
      return Array.from(this.countrySelectTarget.selectedOptions)
        .map(opt => opt.value)
        .filter(Boolean)
    }
    
    return this.countrySelectTarget.value ? [this.countrySelectTarget.value] : []
  }

  getSelectedStates() {
    if (!this.hasStateSelectTarget) return []
    
    if (this.stateSelectTarget.tomselect) {
      const values = this.stateSelectTarget.tomselect.getValue()
      return Array.isArray(values) ? values : (values ? [values] : [])
    }
    
    return Array.from(this.stateSelectTarget.selectedOptions)
      .map(opt => opt.value)
      .filter(Boolean)
  }

  getTagContainer(containerId) {
    return document.getElementById(containerId)
  }

  clearTagContainer(containerId) {
    const container = this.getTagContainer(containerId)
    if (container) {
      container.innerHTML = ''
    }
  }

  async waitForTomSelect(element, maxAttempts = this.RETRIES.TOMSELECT_READY) {
    let attempts = 0
    while (!element?.tomselect && attempts < maxAttempts) {
      await this.delay(this.DELAYS.TOMSELECT_INIT)
      attempts++
    }
    return element?.tomselect
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  shouldShowStateSelection(countries) {
    return countries && countries.length === 1 && this.hasStateContainerTarget
  }

  forceShowStateContainer() {
    if (!this.hasStateContainerTarget) return

    this.stateContainerTarget.classList.remove('hidden')
    if (this.hasHiddenClass) {
      this.stateContainerTarget.classList.remove(this.hiddenClass)
    }
    this.stateContainerTarget.style.display = ''
    this.stateContainerTarget.style.visibility = 'visible'
    this.stateContainerTarget.removeAttribute('hidden')
  }

  updateStateContainerVisibility(savedCountries, currentCountries) {
    if (this.shouldShowStateSelection(savedCountries) && 
        this.shouldShowStateSelection(currentCountries)) {
      this.forceShowStateContainer()
    } else {
      this.hideStateSelection()
    }
  }

  // ==================== Event Handlers ====================

  dispatchFlashError(message, context = 'Location Selection') {
    const event = new CustomEvent('flash:error', {
      detail: { message, context },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }

  dispatchFlashWarning(message) {
    const event = new CustomEvent('flash:warning', {
      detail: { message },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }

  setupCountryChangeListener() {
    if (!this.hasCountrySelectTarget) return

    this.countriesBeforeAdd = this.getSelectedCountries()

    this.countrySelectTarget.addEventListener('change', () => {
      this.countriesBeforeAdd = this.getSelectedCountries()
      this.handleCountryChange(new Event('change'))
    })

    this.element.addEventListener('tom-select:change', (event) => {
      if (event.target === this.countrySelectTarget) {
        this.countriesBeforeAdd = this.getSelectedCountries()
        this.handleCountryChange(event)
      }
    })

    setTimeout(() => {
      const tomSelect = this.countrySelectTarget.tomselect
      if (tomSelect) {
        tomSelect.on('item_add', async (value) => {
          const countriesAfter = this.getSelectedCountries()
          const countriesBefore = this.countriesBeforeAdd?.length === countriesAfter.length - 1
            ? this.countriesBeforeAdd
            : countriesAfter.filter(c => c !== value)
          
          const selectedStates = this.getSelectedStates()
          
          if (countriesBefore.length === 1 && countriesAfter.length >= 2 && selectedStates.length > 0) {
            const statesToRestore = [...selectedStates]
            this.isHandlingCountryWarning = true
            tomSelect.removeItem(value)
            await this.delay(10)
            
            const shouldContinue = await this.showAddCountryWarning()
            
            if (shouldContinue) {
              this.isHandlingCountryWarning = false
              tomSelect.addItem(value)
              this.countriesBeforeAdd = this.getSelectedCountries()
              this.handleCountryChange(new Event('change'))
            } else {
              this.isHandlingCountryWarning = false
              this.ensureCountryRemoved(tomSelect, value)
              this.countriesBeforeAdd = countriesBefore
              this.restoreStates(statesToRestore)
              tomSelect.refreshOptions(false)
            }
            return
          }
          
          this.countriesBeforeAdd = this.getSelectedCountries()
          this.handleCountryChange(new Event('change'))
        })
        
        tomSelect.on('item_remove', () => {
          if (this.isHandlingCountryWarning) return
          this.countriesBeforeAdd = this.getSelectedCountries()
          this.handleCountryChange(new Event('change'))
        })
      }
    }, this.DELAYS.TOMSELECT_INIT)
  }

  ensureCountryRemoved(tomSelect, value) {
    const currentCountries = this.getSelectedCountries()
    if (currentCountries.includes(value)) {
      tomSelect.removeItem(value)
    }
  }

  restoreStates(statesToRestore) {
    if (!this.hasStateSelectTarget || statesToRestore.length === 0) return

    const stateTomSelect = this.stateSelectTarget.tomselect
    if (stateTomSelect) {
      stateTomSelect.setValue(statesToRestore)
    } else {
      statesToRestore.forEach(stateValue => {
        const option = Array.from(this.stateSelectTarget.options).find(opt => opt.value === stateValue)
        if (option) option.selected = true
      })
    }
    
    this.showStateSelection()
  }

  setupStateChangeListener() {
    if (!this.hasStateSelectTarget) return

    this.stateSelectTarget.addEventListener('change', this.handleStateChange.bind(this))
    this.element.addEventListener('tom-select:change', (event) => {
      if (event.target === this.stateSelectTarget) {
        this.handleStateChange(event)
      }
    })
  }

  async handleCountryChange(event) {
    const selectedCountries = this.getSelectedCountries()

    try {
      this.clearStateSelection()

      if (selectedCountries.length === 0) {
        this.hideStateSelection()
        return
      }

      if (selectedCountries.length === 1) {
        await this.loadStates(selectedCountries[0])
        const verifyCountries = this.getSelectedCountries()
        
        if (verifyCountries.length === 1 && this.hasStateContainerTarget) {
          this.showElement(this.stateContainerTarget)
        } else {
          this.hideStateSelection()
        }
      } else {
        this.hideStateSelection()
        this.clearStateSelection()
      }
    } catch (error) {
      this.dispatchFlashError(`Failed to load location data: ${error.message}`)
    }
  }

  handleStateChange(event) {
    // State change handler - no additional logic needed
  }

  // ==================== State Management ====================

  async loadStates(countryCode) {
    if (!this.hasStateSelectTarget) return

    try {
      this.showStateLoading()
      this.clearTagContainer('states-showlist')

      const response = await fetch(`${this.statesUrlValue}?country=${countryCode}`)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const states = await response.json()
      this.populateStates(states)
    } catch (error) {
      this.hideStateSelection()
      throw error
    } finally {
      this.hideStateLoading()
    }
  }

  populateStates(states) {
    if (!this.hasStateSelectTarget) return

    const options = states
      .map(state => {
        const value = typeof state === 'string' ? state : (state.value || state.code || state.name || '')
        const name = typeof state === 'string' ? state : (state.name || state.value || state.code || '')
        return value && name ? { value, name } : null
      })
      .filter(Boolean)

    const tomSelect = this.stateSelectTarget.tomselect
    
    if (tomSelect) {
      tomSelect.clearOptions()
      tomSelect.clear()
      tomSelect.setValue('')
    }

    // Clear and repopulate DOM select
    this.stateSelectTarget.innerHTML = ''
    options.forEach(option => {
      const optionElement = document.createElement('option')
      optionElement.value = option.value
      optionElement.textContent = option.name
      optionElement.setAttribute('data-name', option.name)
      this.stateSelectTarget.appendChild(optionElement)
    })

    // Sync with TomSelect if present
    if (tomSelect) {
      Array.from(this.stateSelectTarget.options).forEach(option => {
        tomSelect.addOption({
          value: option.value,
          name: option.textContent || option.getAttribute('data-name') || option.value
        })
      })
      tomSelect.refreshOptions(true)
    }
  }

  clearStateSelection() {
    if (!this.hasStateSelectTarget) return

    this.stateSelectTarget.value = ''
    this.stateSelectTarget.selectedIndex = 0
    
    Array.from(this.stateSelectTarget.options).forEach(option => {
      option.selected = false
    })

    const tomSelect = this.stateSelectTarget.tomselect
    if (tomSelect) {
      tomSelect.setValue([])
      tomSelect.clearOptions()
      tomSelect.clear()
      tomSelect.refreshOptions(false)
    }
  }

  showStateSelection() {
    if (this.getSelectedCountries().length !== 1) return
    if (this.hasStateContainerTarget) {
      this.showElement(this.stateContainerTarget)
    }
  }

  hideStateSelection() {
    if (this.hasStateContainerTarget) {
      this.hideElement(this.stateContainerTarget)
    }
    this.clearStateSelection()
  }

  showStateLoading() {
    if (this.hasStateSelectTarget && this.hasLoadingClass) {
      this.stateSelectTarget.classList.add(this.loadingClass)
    }
  }

  hideStateLoading() {
    if (this.hasStateSelectTarget && this.hasLoadingClass) {
      this.stateSelectTarget.classList.remove(this.loadingClass)
    }
  }

  async restoreSavedStates() {
    if (!this.hasStateSelectTarget) return

    const hasSavedState = this.stateSelectTarget.dataset.selected
    if (!hasSavedState) return

    const savedState = this.stateSelectTarget.dataset.selected
    const stateValues = savedState.includes(',') 
      ? savedState.split(',').map(s => s.trim()) 
      : [savedState]
    
    if (this.stateSelectTarget.tomselect) {
      this.stateSelectTarget.tomselect.setValue(stateValues)
    } else {
      Array.from(this.stateSelectTarget.options).forEach(option => {
        option.selected = stateValues.includes(option.value)
      })
    }
  }

  // ==================== Location Selection ====================

  validateLocationSelection() {
    if (!this.hasCountrySelectTarget) {
      this.dispatchFlashError('Country selection not available', 'Form Validation')
      return null
    }

    const selectedCountries = this.getSelectedCountries()
    const countries = selectedCountries.map(code => {
      const option = Array.from(this.countrySelectTarget.options).find(opt => opt.value === code)
      return {
        value: code,
        text: option?.textContent?.trim() || code
      }
    })

    let stateValue = null
    let stateText = null

    if (selectedCountries.length === 1 && this.hasStateSelectTarget) {
      const selectedStates = this.getSelectedStates()
      const stateOptions = selectedStates.map(val => {
        const option = Array.from(this.stateSelectTarget.options).find(opt => opt.value === val)
        return option?.textContent?.trim() || val
      })
      
      stateValue = selectedStates.length > 0 ? selectedStates.join(',') : null
      stateText = stateOptions.join(', ')
    }

    return {
      countries: countries,
      state: stateValue ? { value: stateValue, text: stateText } : null
    }
  }

  // ==================== UI Actions ====================

  async toggleLocationSelector() {
    try {
      if (!this.hasLocationSelectorTarget) {
        this.dispatchFlashError('Location selector not available')
        return
      }

      await this.restoreSavedLocation()
      this.updateStateContainerVisibilityAfterRestore()
      this.showElement(this.locationSelectorTarget)
    } catch (error) {
      this.dispatchFlashError(`Error showing location selector: ${error.message}`)
    }
  }

  async editLocation() {
    try {
      if (!this.hasLocationSelectorTarget) {
        this.dispatchFlashError('Location selector not available')
        return
      }

      await this.restoreSavedLocation()
      this.updateStateContainerVisibilityAfterRestore()

      this.showElement(this.locationSelectorTarget)
      this.hideElement(this.displayLocationTarget)
      this.hideElement(this.selectorButtonTarget)

      this.dispatch('locationEditing')
    } catch (error) {
      this.dispatchFlashError(`Error editing location: ${error.message}`)
    }
  }

  updateStateContainerVisibilityAfterRestore() {
    const currentLocation = this.getCurrentLocation()
    const selectedCountries = this.getSelectedCountries()
    
    this.updateStateContainerVisibility(
      currentLocation?.countries,
      selectedCountries
    )
  }

  saveLocation() {
    try {
      const selectedLocation = this.validateLocationSelection()
      if (!selectedLocation) return

      this.updateFormFields(selectedLocation)
      this.updateDisplayContent(selectedLocation)

      this.hideElement(this.locationSelectorTarget)
      this.showElement(this.displayLocationTarget)
      this.hideElement(this.selectorButtonTarget)
      this.dispatch('locationSaved')
    } catch (error) {
      this.dispatchFlashError(`Error saving location: ${error.message}`)
      this.handleSaveError()
    }
  }

  async cancelLocation(event) {
    if (event) {
      this.clearTagContainer('countries-showlist')
      this.clearTagContainer('states-showlist')
      event.preventDefault()
      event.stopPropagation()
    }
    
    try {
      await this.restoreSavedLocation()
      
      this.hideElement(this.locationSelectorTarget)

      const currentLocation = this.getCurrentLocation()
      if (currentLocation) {
        this.showElement(this.displayLocationTarget)
        this.hideElement(this.selectorButtonTarget)
      } else {
        this.hideElement(this.displayLocationTarget)
        this.showElement(this.selectorButtonTarget)
      }

      this.dispatch('locationCancelled')
    } catch (error) {
      this.dispatchFlashError(`Error cancelling location selection: ${error.message}`)
    }
  }

  resetLocation() {
    try {
      this.clearTagContainer('countries-showlist')
      
      if (this.hasCountrySelectTarget) {
        if (this.countrySelectTarget.tomselect) {
          this.countrySelectTarget.tomselect.setValue('')
        } else {
          this.countrySelectTarget.selectedIndex = 0
        }
      }

      this.hideStateSelection()

      if (this.hasLocationTextTarget) {
        this.locationTextTarget.textContent = ''
      }

      this.hideElement(this.displayLocationTarget)
      this.hideElement(this.locationSelectorTarget)
      this.showElement(this.selectorButtonTarget)

      this.dispatch('locationReset')
    } catch (error) {
      this.dispatchFlashError(`Error resetting location: ${error.message}`)
    }
  }

  // ==================== Form Field Updates ====================

  updateFormFields(location) {
    const countryValues = location.countries.map(c => c.value)
    const countryValueStr = countryValues.length > 0 ? countryValues.join(',') : ''
    
    // Update dataset.selected on select elements
    if (this.hasCountrySelectTarget) {
      this.countrySelectTarget.dataset.selected = countryValueStr
      
      // Update select_tag fields directly for form submission
      if (this.countrySelectTarget.multiple) {
        Array.from(this.countrySelectTarget.options).forEach(option => {
          option.selected = countryValues.includes(option.value)
        })
      }
    }

    if (this.hasStateSelectTarget) {
      if (location.countries.length === 1 && location.state) {
        this.stateSelectTarget.dataset.selected = location.state.value
        
        // Update select_tag fields directly for form submission
        const stateValues = location.state.value.includes(',')
          ? location.state.value.split(',').map(s => s.trim())
          : [location.state.value]
        
        if (this.stateSelectTarget.multiple) {
          Array.from(this.stateSelectTarget.options).forEach(option => {
            option.selected = stateValues.includes(option.value)
          })
        }
      } else {
        this.stateSelectTarget.dataset.selected = ''
      }
    }
  }

  updateDisplayContent(location) {
    const locationParts = []
    
    if (!location?.countries || location.countries.length === 0) {
      const displayText = 'All Locations'
      if (this.hasLocationTextTarget) {
        this.locationTextTarget.textContent = displayText
        this.locationTextTarget.dataset.countryValue = ''
        this.locationTextTarget.dataset.countryText = 'All countries'
        delete this.locationTextTarget.dataset.stateValue
        delete this.locationTextTarget.dataset.stateText
      }
      return
    }
    
    if (location.state && location.state.value !== 'all') {
      locationParts.push(location.state.text)
    }
    
    const countryNames = location.countries.map(c => c.text)
    const countryDisplayText = location.countries.length === 1
      ? countryNames[0]
      : `${countryNames[0]} +${location.countries.length - 1} countries`
    
    locationParts.push(countryDisplayText)
    const displayText = locationParts.join(', ')

    if (this.hasLocationTextTarget) {
      this.locationTextTarget.textContent = displayText

      const countryValues = location.countries.map(c => c.value)
      this.locationTextTarget.dataset.countryValue = countryValues.join(',')
      this.locationTextTarget.dataset.countryText = countryNames.join(', ')

      if (location.state) {
        this.locationTextTarget.dataset.stateValue = location.state.value
        this.locationTextTarget.dataset.stateText = location.state.text
      } else {
        delete this.locationTextTarget.dataset.stateValue
        delete this.locationTextTarget.dataset.stateText
      }
    } else {
      this.dispatchFlashWarning('Location text target not found - text not updated')
    }
  }

  // ==================== Location Restoration ====================

  getCurrentLocation() {
    // Try dataset first (JavaScript-saved location)
    if (this.hasLocationTextTarget) {
      const data = this.locationTextTarget.dataset
      const displayText = this.locationTextTarget.textContent?.trim() || ''

      if (data.countryValue !== undefined || data.stateValue !== undefined) {
        const countryValueStr = data.countryValue || ''
        const countryTextStr = data.countryText || ''
        
        const countryValues = countryValueStr 
          ? countryValueStr.split(',').map(v => v.trim()).filter(Boolean) 
          : []
        const countryTexts = countryTextStr 
          ? countryTextStr.split(', ').map(t => t.trim()).filter(Boolean) 
          : []

        const countries = countryValues.length > 0 
          ? countryValues.map((value, index) => ({
              value: value,
              text: countryTexts[index] || value
            }))
          : []

        return {
          countries: countries,
          state: data.stateValue ? {
            value: data.stateValue,
            text: data.stateText
          } : null,
          displayText: displayText
        }
      }
    }
    
    // Fallback: Read from select element dataset
    if (this.hasCountrySelectTarget) {
      const countryValueStr = this.countrySelectTarget.dataset.selected || ''
      const stateValueStr = this.hasStateSelectTarget ? (this.stateSelectTarget.dataset.selected || '') : ''
      
      const countryValues = countryValueStr 
        ? countryValueStr.split(',').map(v => v.trim()).filter(Boolean) 
        : []
      
      const countryTexts = countryValues.map(code => {
        const option = Array.from(this.countrySelectTarget.options).find(opt => opt.value === code)
        return option?.textContent?.trim() || code
      })

      const countries = countryValues.length > 0 
        ? countryValues.map((value, index) => ({
            value: value,
            text: countryTexts[index] || value
          }))
        : []

      return {
        countries: countries,
        state: stateValueStr ? {
          value: stateValueStr,
          text: stateValueStr
        } : null,
        displayText: this.hasLocationTextTarget ? this.locationTextTarget.textContent?.trim() || '' : ''
      }
    }
    
    return null
  }

  async restoreSavedLocation() {
    try {
      const currentLocation = this.getCurrentLocation()
      
      if (!currentLocation) {
        await this.clearCountrySelection()
        this.hideStateSelection()
        return
      }

      await this.restoreCountrySelection(currentLocation.countries)
      await this.restoreStateSelection(currentLocation)
    } catch (error) {
      this.dispatchFlashError(`Error restoring saved location: ${error.message}`)
    }
  }

  async clearCountrySelection() {
    this.clearTagContainer('countries-showlist')
    
    if (this.hasCountrySelectTarget) {
      if (this.countrySelectTarget.tomselect) {
        this.countrySelectTarget.tomselect.setValue('')
      } else {
        Array.from(this.countrySelectTarget.options).forEach(option => {
          option.selected = false
        })
      }
    }
  }

  async restoreCountrySelection(countries) {
    if (!this.hasCountrySelectTarget) return

    const countryValues = countries.map(c => c.value)
    
    this.clearTagContainer('countries-showlist')
    
    if (this.countrySelectTarget.tomselect) {
      const tomSelect = this.countrySelectTarget.tomselect
      
      // Clear existing tags and selection
      this.clearTagContainer('countries-showlist')
      tomSelect.clear()
      
      // Wait for TomSelect to sync
      await this.delay(this.DELAYS.TOMSELECT_SYNC)
      
      // Double-check container is still clear
      const container = this.getTagContainer('countries-showlist')
      if (container && container.children.length > 0) {
        container.innerHTML = ''
      }
      
      // Set new values
      if (countryValues.length > 0) {
        tomSelect.setValue(countryValues)
      } else {
        tomSelect.setValue([])
      }
    } else {
      // Regular select multiple
      Array.from(this.countrySelectTarget.options).forEach(option => {
        option.selected = countryValues.includes(option.value)
      })
    }
  }

  async restoreStateSelection(currentLocation) {
    const selectedCountries = this.getSelectedCountries()
    
    // Only restore state if single country is selected
    if (!this.shouldShowStateSelection(currentLocation.countries) ||
        !this.shouldShowStateSelection(selectedCountries) ||
        !this.hasStateSelectTarget) {
      this.clearStateSelection()
      this.hideStateSelection()
      return
    }

    // Wait for country selection to sync
    await this.delay(this.DELAYS.TOMSELECT_SYNC)
    
    // Load states for the selected country
    await this.loadStates(currentLocation.countries[0].value)
    
    // Final check before showing state container
    const finalCountryCheck = this.getSelectedCountries()
    if (finalCountryCheck.length === 1 && this.hasStateContainerTarget) {
      this.forceShowStateContainer()
    } else {
      this.hideStateSelection()
      return
    }
    
    // Wait for TomSelect to be ready
    await this.waitForTomSelect(this.stateSelectTarget)

    // Restore state values
    if (currentLocation.state && currentLocation.state.value !== 'all') {
      const stateValue = currentLocation.state.value
      const stateValues = stateValue.includes(',')
        ? stateValue.split(',').map(s => s.trim()).filter(v => v)
        : (stateValue ? [stateValue] : [])
      
      if (stateValues.length > 0) {
        if (this.stateSelectTarget.tomselect) {
          await this.delay(this.DELAYS.STATE_LOAD)
          this.stateSelectTarget.tomselect.setValue(stateValues)
        } else {
          Array.from(this.stateSelectTarget.options).forEach(option => {
            option.selected = stateValues.includes(option.value)
          })
        }
      }
    } else {
      // Clear state selection (empty = all states)
      if (this.stateSelectTarget.tomselect) {
        this.stateSelectTarget.tomselect.setValue([])
      } else {
        Array.from(this.stateSelectTarget.options).forEach(option => {
          option.selected = false
        })
      }
    }
  }

  // ==================== Utility Methods ====================

  setLocation(countryValues, countryTexts, stateValue = null, stateText = null) {
    try {
      const countries = Array.isArray(countryValues) 
        ? countryValues.map((value, index) => ({
            value,
            text: countryTexts?.[index] || value
          }))
        : [{ value: countryValues, text: countryTexts || countryValues }]

      const location = {
        countries: countries,
        state: stateValue ? { value: stateValue, text: stateText } : null
      }

      this.updateDisplayContent(location)
      this.showElement(this.displayLocationTarget)
      this.hideElement(this.selectorButtonTarget)
      this.hideElement(this.locationSelectorTarget)
    } catch (error) {
      this.dispatchFlashError(`Error setting location: ${error.message}`)
    }
  }

  showElement(element) {
    if (!element) {
      throw new Error('Element not found')
    }

    if (this.hasHiddenClass) {
      element.classList.remove(this.hiddenClass)
    } else {
      element.classList.remove('hidden')
    }
  }

  hideElement(element) {
    if (!element) {
      throw new Error('Element not found')
    }

    if (this.hasHiddenClass) {
      element.classList.add(this.hiddenClass)
    } else {
      element.classList.add('hidden')
    }
  }

  validateRequiredTargets() {
    const requiredTargets = [
      'locationSelector',
      'displayLocation',
      'countrySelect',
      'selectorButton'
    ]

    const missingTargets = requiredTargets.filter(target => {
      const hasTarget = this[`has${this.capitalize(target)}Target`]
      return !hasTarget
    })

    if (missingTargets.length > 0) {
      this.dispatchFlashError(
        `Missing required targets: ${missingTargets.join(', ')}`,
        'Controller Setup'
      )
      return false
    }

    return true
  }

  handleSaveError() {
    try {
      this.cancelLocation()
    } catch (fallbackError) {
      this.dispatchFlashError('Critical error in location handling', 'System Error')
    }
  }

  capitalize(string) {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  async showAddCountryWarning() {
    return new Promise((resolve) => {
      const modal = document.getElementById('add-country-warning-modal')
      if (!modal) {
        console.error('Add country warning modal not found')
        resolve(false)
        return
      }

      const continueBtn = document.getElementById('continue-add-country')
      const cancelBtn = document.getElementById('cancel-add-country')

      const cleanup = () => {
        modal.classList.add('hidden')
        modal.setAttribute('aria-hidden', 'true')
        document.removeEventListener('keydown', escapeHandler)
        if (continueBtn) continueBtn.removeEventListener('click', handleContinue)
        if (cancelBtn) cancelBtn.removeEventListener('click', handleCancel)
        modal.removeEventListener('click', handleOverlayClick)
      }

      const handleContinue = () => {
        cleanup()
        resolve(true)
      }

      const handleCancel = () => {
        cleanup()
        resolve(false)
      }

      const handleOverlayClick = (e) => {
        if (e.target === modal) {
          cleanup()
          resolve(false)
        }
      }

      const escapeHandler = (e) => {
        if (e.key === 'Escape') {
          cleanup()
          resolve(false)
        }
      }

      modal.classList.remove('hidden')
      modal.setAttribute('aria-hidden', 'false')

      if (continueBtn) continueBtn.addEventListener('click', handleContinue)
      if (cancelBtn) cancelBtn.addEventListener('click', handleCancel)
      modal.addEventListener('click', handleOverlayClick)
      document.addEventListener('keydown', escapeHandler)
    })
  }

  disconnect() {
    if (this.hasCountrySelectTarget) {
      this.countrySelectTarget.removeEventListener('change', this.handleCountryChange.bind(this))
    }
    if (this.hasStateSelectTarget) {
      this.stateSelectTarget.removeEventListener('change', this.handleStateChange.bind(this))
    }
  }
}
